import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { apiService } from "../../service/Api";
import { Loader2, Printer, Share2, Check, X, Copy, MessageSquare, Edit3 } from "lucide-react";
import HtmlPreview from "../../components/htmlCollateral/HtmlPreview";
import HtmlChatbot from "../../components/htmlCollateral/HtmlChatbot";
import HtmlInspectorPanel from "../../components/htmlCollateral/HtmlInspectorPanel";
import SaveChangesDialog from "../../components/visualEditor/SaveChangesDialog";
import HtmlHistory from "../../utils/htmlHistory";
import { useAuth } from "../../context/AuthContext";
import html2pdf from "html2pdf.js";

export default function HtmlCollateral() {
    const { slug } = useParams();
    const [htmlCode, setHtmlCode] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { user } = useAuth();

    const previewRef = useRef(null);
    const historyManager = useRef(new HtmlHistory());

    // Version management
    const [allVersions, setAllVersions] = useState([]);
    const [currentVersionIndex, setCurrentVersionIndex] = useState(0);

    // Chatbot states
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const [isSending, setIsSending] = useState(false);

    // Shareable link state
    const [linkCopied, setLinkCopied] = useState(false);
    const [showLinkModal, setShowLinkModal] = useState(false);
    const [shareableLink, setShareableLink] = useState('');

    // Sidebar state
    const [sidebarMode, setSidebarMode] = useState('chat'); // 'chat' | 'edit'

    // Visual editor states
    const [selectedElement, setSelectedElement] = useState(null);
    const [isDragMode, setIsDragMode] = useState(false);
    const [updateSource, setUpdateSource] = useState('initial'); // 'initial', 'chat', 'editor', 'history'

    // Undo/Redo states
    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);

    // Save functionality states
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [showSaveDialog, setShowSaveDialog] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const originalHtmlRef = useRef(null);

    const extractVersionContent = (version) => {
        const nested = version?.collateral?.collateral;
        if (nested && typeof nested === "string") {
            return nested;
        }

        const direct = version?.collateral;
        if (direct && typeof direct === "string") {
            return direct;
        }

        return version?.code || "";
    };

    useEffect(() => {
        if (!slug) return;
        setLoading(true);
        setError(null);

        async function fetchData() {
            try {
                const response = await apiService.gettailspinCollateralBySlug(slug);

                console.log("Response:", response);

                // Handle array response (multiple versions)
                const versions = Array.isArray(response?.data?.data)
                    ? response.data.data
                    : [response?.data?.data].filter(Boolean);

                if (versions.length === 0) {
                    setError("No collateral data found.");
                    return;
                }

                setAllVersions(versions);
                setCurrentVersionIndex(0);

                // Build chat history from versions
                const historyMessages = [];
                const chronologicalVersions = [...versions].reverse();

                chronologicalVersions.forEach((version, index) => {
                    const description = version.description;

                    if (description) {
                        historyMessages.push({
                            id: `hist-user-${version.id || index}`,
                            type: 'user',
                            content: description,
                            timestamp: new Date(version.created_at || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                        });

                        historyMessages.push({
                            id: `hist-bot-${version.id || index}`,
                            type: 'bot',
                            content: `✓ Version ${index + 1} generated successfully.`,
                            timestamp: new Date(version.created_at || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                        });
                    }
                });

                setMessages(historyMessages);

                // Set initial HTML code from index 0
                const initialVersion = versions[0];
                const initialCode = extractVersionContent(initialVersion);
                setUpdateSource('initial');
                setHtmlCode(initialCode);

                // Initialize history
                historyManager.current.push(initialCode);
                updateHistoryButtons();

                // Store original HTML for save comparison
                originalHtmlRef.current = initialCode;

                console.log("Full versions:", versions);
                console.log("Showing version:", 0);
            } catch (err) {
                console.error(err);
                setError("Failed to fetch HTML collateral.");
            } finally {
                setLoading(false);
            }
        }

        fetchData();
    }, [slug]);

    // Keyboard shortcuts for Undo/Redo
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
                e.preventDefault();
                if (e.shiftKey) {
                    handleRedo();
                } else {
                    handleUndo();
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [canUndo, canRedo]); // Dependencies needed for closures if not using refs for handlers, but handlers use refs internally so it might be fine, but safer to include dependencies or wrap handlers in useCallback properly.
    // Actually handleUndo/Redo use historyManager.current which is a ref, and setHtmlCode.
    // Ideally handleUndo/Redo should be stable or we depend on them.
    // Let's verify handleUndo/Redo definitions. They are defined in the component scope.
    // They don't use any state other than setHtmlCode/setUpdateSource/updateHistoryButtons.
    // So they are recreated on every render.
    // Better to wrap them in useCallback or just include them in dependency array.
    // For now, I'll include them in the dependency array implicitly by not memoizing them, or I can just disable the lint rule if I'm lazy, but let's do it right.
    // Wait, handleUndo/Redo are NOT wrapped in useCallback in the current file.
    // So they change on every render.
    // So I should include them in the useEffect dependency array.

    const updateHistoryButtons = useCallback(() => {
        setCanUndo(historyManager.current.canUndo());
        setCanRedo(historyManager.current.canRedo());
    }, []);

    const handleVersionSwitch = async (index) => {
        if (index >= 0 && index < allVersions.length) {
            setCurrentVersionIndex(index);
            const version = allVersions[index];
            const versionCode = extractVersionContent(version);
            setUpdateSource('history');
            setHtmlCode(versionCode);

            // Add to history
            historyManager.current.push(versionCode);
            updateHistoryButtons();
        }
    };

    const handleUndo = () => {
        const previousState = historyManager.current.undo();
        if (previousState) {
            setUpdateSource('history');
            setHtmlCode(previousState);
            updateHistoryButtons();
        }
    };

    const handleRedo = () => {
        const nextState = historyManager.current.redo();
        if (nextState) {
            setUpdateSource('history');
            setHtmlCode(nextState);
            updateHistoryButtons();
        }
    };

    const handleSendMessage = async () => {
        if (!inputValue.trim() || isSending) return;

        const messageContent = inputValue.trim();

        const userMessage = {
            id: Date.now(),
            type: "user",
            content: messageContent,
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };

        setMessages(prev => [...prev, userMessage]);
        setInputValue("");
        setIsSending(true);

        try {
            // Use edit-code endpoint with slug and version index
            const response = await apiService.editCodeCollateral(slug, messageContent, currentVersionIndex);

            if (response?.data?.success) {
                // Refresh all versions after edit
                const refreshResponse = await apiService.gettailspinCollateralBySlug(slug);
                const versions = Array.isArray(refreshResponse?.data?.data)
                    ? refreshResponse.data.data
                    : [refreshResponse?.data?.data].filter(Boolean);

                setAllVersions(versions);
                setCurrentVersionIndex(0); // New version is always at index 0

                // Update preview with new version
                const newVersion = versions[0];
                const newCode = extractVersionContent(newVersion);
                setUpdateSource('chat');
                setHtmlCode(newCode);

                // Add to history
                historyManager.current.push(newCode);
                updateHistoryButtons();

                // Reset original HTML reference
                originalHtmlRef.current = newCode;
                setHasUnsavedChanges(false);

                const botMessage = {
                    id: Date.now() + 1,
                    type: "bot",
                    content: "✓ HTML updated successfully! The preview has been refreshed with your changes.",
                    timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                };

                setMessages(prev => [...prev, botMessage]);
            } else {
                throw new Error("Failed to update HTML");
            }
        } catch (err) {
            console.error("Error editing HTML:", err);

            const errorMessage = {
                id: Date.now() + 1,
                type: "bot",
                content: err.response?.data?.error || "❌ Sorry, I couldn't process your request. Please try again.",
                timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                isError: true
            };

            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsSending(false);
        }
    };

    const handleInputChange = (e) => setInputValue(e.target.value);

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleTabChange = (mode) => {
        if (mode === 'chat' && sidebarMode === 'edit' && hasUnsavedChanges) {
            setShowSaveDialog(true);
            return;
        }

        setSidebarMode(mode);
        if (mode === 'edit') {
            // Enable visual edit mode - selection will be enabled by HtmlPreview
        } else {
            // Disable visual edit mode
            setSelectedElement(null);
            setIsDragMode(false);
        }
    };

    const handleElementSelect = useCallback((element) => {
        console.log('HtmlCollateral: handleElementSelect called', element);
        setSelectedElement(element);
    }, []);

    const handleUpdateProperty = useCallback((property, value) => {
        if (!selectedElement) return;

        // Store original HTML on first edit
        if (originalHtmlRef.current === htmlCode) {
            originalHtmlRef.current = htmlCode;
        }

        if (property === 'content') {
            if (selectedElement.tagName === 'INPUT' || selectedElement.tagName === 'TEXTAREA') {
                selectedElement.value = value;
                selectedElement.placeholder = value;
            } else if (selectedElement.tagName === 'IMG') {
                selectedElement.src = value;
            } else {
                selectedElement.innerText = value;
            }
        } else {
            selectedElement.style[property] = value;
        }

        // Update HTML code and history
        if (previewRef.current) {
            const newCode = previewRef.current.innerHTML;
            setUpdateSource('editor');
            setHtmlCode(newCode);
            historyManager.current.push(newCode);
            updateHistoryButtons();
            
            // Mark as unsaved
            setHasUnsavedChanges(true);
        }
    }, [selectedElement, htmlCode, updateHistoryButtons]);

    const handleInsertImage = useCallback(() => {
        if (!selectedElement) return;

        const defaultUrl = `https://images.unsplash.com/photo-1557683316-973673baf926?auto=format&fit=crop&w=400&q=80`;
        const url = prompt("Enter Image URL:", defaultUrl);

        if (url) {
            const img = document.createElement('img');
            img.src = url;
            img.className = "w-full h-48 object-cover rounded-lg shadow-md mt-2 hover:scale-105 transition-transform duration-300";

            if (['IMG', 'INPUT', 'HR', 'BR'].includes(selectedElement.tagName)) {
                selectedElement.parentNode.insertBefore(img, selectedElement.nextSibling);
            } else {
                selectedElement.appendChild(img);
            }

            // Update HTML code and history
            if (previewRef.current) {
                const newCode = previewRef.current.innerHTML;
                setUpdateSource('editor');
                setHtmlCode(newCode);
                historyManager.current.push(newCode);
                updateHistoryButtons();
                
                // Mark as unsaved
                setHasUnsavedChanges(true);
            }
        }
    }, [selectedElement, updateHistoryButtons]);

    const handleDeleteElement = useCallback(() => {
        if (selectedElement) {
            selectedElement.remove();
            setSelectedElement(null);

            // Update HTML code and history
            if (previewRef.current) {
                const newCode = previewRef.current.innerHTML;
                setUpdateSource('editor');
                setHtmlCode(newCode);
                historyManager.current.push(newCode);
                updateHistoryButtons();
                
                // Mark as unsaved
                setHasUnsavedChanges(true);
            }
        }
    }, [selectedElement, updateHistoryButtons]);

    const handleHtmlChange = useCallback((newHtml) => {
        setUpdateSource('editor');
        setHtmlCode(newHtml);
        historyManager.current.push(newHtml);
        updateHistoryButtons();
        
        // Mark as unsaved
        setHasUnsavedChanges(true);
    }, [updateHistoryButtons]);

    const handleGetShareableLink = () => {
        if (!slug) return;
        const baseUrl = process.env.REACT_APP_SHAREABLE_URL || window.location.origin;
        const link = `${baseUrl}/html/${slug}`;
        setShareableLink(link);
        setShowLinkModal(true);
    };

    const handleCopyLink = async () => {
        if (!shareableLink) return;

        try {
            await navigator.clipboard.writeText(shareableLink);
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 2000);
        } catch (err) {
            const textArea = document.createElement('textarea');
            textArea.value = shareableLink;
            textArea.style.position = 'fixed';
            textArea.style.opacity = '0';
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                setLinkCopied(true);
                setTimeout(() => setLinkCopied(false), 2000);
            } catch (fallbackErr) {
                console.error('Failed to copy link:', fallbackErr);
                alert(`Shareable link: ${shareableLink}`);
            }
            document.body.removeChild(textArea);
        }
    };

    const handleDownloadPDF = () => {
        if (!previewRef.current) return;

        const element = previewRef.current;
        const opt = {
            margin: 0,
            filename: 'html-collateral.pdf',
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true },
            jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
        };

        html2pdf().set(opt).from(element).save();
    };

    const toggleDragMode = () => {
        setIsDragMode(!isDragMode);
        if (!isDragMode) {
            setSelectedElement(null);
        }
    };

    /**
     * Save changes to backend
     */
    const handleSaveChanges = async () => {
        setIsSaving(true);

        try {
            const currentCollateral = allVersions[currentVersionIndex];
            if (!currentCollateral?.id) {
                throw new Error('No collateral ID found');
            }

            console.log('💾 Saving HTML collateral changes...', {
                id: currentCollateral.id,
                hasHtmlCode: !!htmlCode
            });

            const response = await apiService.updateHtmlCollateral(
                currentCollateral.id,
                htmlCode
            );

            if (response?.data?.success) {
                console.log('✅ HTML collateral saved successfully');

                // Update original HTML reference
                originalHtmlRef.current = htmlCode;

                // Clear unsaved changes flag
                setHasUnsavedChanges(false);
                setShowSaveDialog(false);
                
                // Show success message (you could add a toast notification here)
                const successMessage = {
                    id: Date.now(),
                    type: "bot",
                    content: "✓ Changes saved successfully!",
                    timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                };
                setMessages(prev => [...prev, successMessage]);
            } else {
                throw new Error(response?.data?.message || 'Failed to save');
            }
        } catch (error) {
            console.error('❌ Error saving HTML collateral:', error);
            
            const errorMessage = {
                id: Date.now(),
                type: "bot",
                content: `❌ Failed to save: ${error.message}`,
                timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                isError: true
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsSaving(false);
        }
    };

    /**
     * Discard changes and revert to original HTML
     */
    const handleDiscardChanges = () => {
        if (originalHtmlRef.current) {
            setHtmlCode(originalHtmlRef.current);
            historyManager.current.push(originalHtmlRef.current);
            updateHistoryButtons();
        }
        setHasUnsavedChanges(false);
        setShowSaveDialog(false);
        setSelectedElement(null);
        // Switch to chat mode
        setSidebarMode('chat');
    };

    /**
     * Cancel closing (stay in edit mode)
     */
    const handleCancelClose = () => {
        setShowSaveDialog(false);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: '#F8F7F4' }}>
                <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" style={{ color: '#FF6B5A' }} />
                    <p style={{ color: '#0A0A10' }}>Loading HTML collateral...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: '#F8F7F4' }}>
                <div className="text-center p-6 rounded-lg" style={{ color: '#FF6B5A', backgroundColor: 'rgba(255, 107, 90, 0.1)' }}>
                    <p className="font-semibold">{error}</p>
                </div>
            </div>
        );
    }

    if (!htmlCode) {
        return (
            <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: '#F8F7F4' }}>
                <div style={{ color: '#0A0A10' }}>No HTML code received.</div>
            </div>
        );
    }

    return (
        <div className="flex h-screen" style={{ backgroundColor: '#F8F7F4' }}>
            {/* Left Sidebar - Chatbot or Inspector - FIXED WIDTH */}
            <div className="w-96 flex flex-col flex-shrink-0 border-r border-gray-200 bg-white">
                {/* Tabs */}
                <div className="flex border-b border-gray-200">
                    <button
                        onClick={() => handleTabChange('chat')}
                        className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${sidebarMode === 'chat' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
                    >
                        <MessageSquare className="w-4 h-4" />
                        Chat
                    </button>
                    <button
                        onClick={() => handleTabChange('edit')}
                        className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${sidebarMode === 'edit' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
                    >
                        <Edit3 className="w-4 h-4" />
                        Editor
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-hidden relative">
                    <div className={`absolute inset-0 flex flex-col transition-opacity duration-200 ${sidebarMode === 'chat' ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'}`}>
                        <HtmlChatbot
                            messages={messages}
                            inputValue={inputValue}
                            isSending={isSending}
                            onInputChange={handleInputChange}
                            onSendMessage={handleSendMessage}
                            onKeyPress={handleKeyPress}
                            allVersions={allVersions}
                            currentVersionIndex={currentVersionIndex}
                            onVersionSwitch={handleVersionSwitch}
                            onUndo={handleUndo}
                            onRedo={handleRedo}
                            canUndo={canUndo}
                            canRedo={canRedo}
                        />
                    </div>

                    {sidebarMode === 'edit' && (
                        <div className="absolute inset-0 z-10 h-full overflow-auto bg-white">
                            <HtmlInspectorPanel
                                selectedElement={selectedElement}
                                onUpdateProperty={handleUpdateProperty}
                                onClose={() => setSelectedElement(null)}
                                onInsertImage={handleInsertImage}
                                onDeleteElement={handleDeleteElement}
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Right Side - HTML Preview - TAKES REMAINING SPACE */}
            <div className="flex-1 overflow-y-auto border-l" style={{ backgroundColor: '#F8F7F4', borderColor: '#D7B472' }}>
                {/* Header Toolbar */}
                <div className="sticky top-0 z-10 border-b px-6 py-4 flex items-center justify-between no-print" style={{ backgroundColor: '#F8F7F4', borderColor: '#D7B472' }}>
                    <h2 className="text-lg font-semibold" style={{ color: '#0A0A10' }}>HTML Preview</h2>
                    <div className="flex items-center gap-3">
                        {hasUnsavedChanges && (
                            <button
                                onClick={() => setShowSaveDialog(true)}
                                className="flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-colors shadow-sm hover:shadow-md font-medium text-sm bg-green-600 hover:bg-green-700"
                            >
                                Save Changes
                            </button>
                        )}
                        <button
                            onClick={toggleDragMode}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors shadow-sm hover:shadow-md font-medium text-sm ${isDragMode
                                ? 'bg-green-600 text-white hover:bg-green-700'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                        >
                            {isDragMode ? 'Done Moving' : 'Move Elements'}
                        </button>
                        <button
                            onClick={handleGetShareableLink}
                            className="flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-colors shadow-sm hover:shadow-md font-medium text-sm"
                            style={{ backgroundColor: '#FF6B5A' }}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e55a4a'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#FF6B5A'; }}
                            title="Get shareable link"
                        >
                            <Share2 className="w-4 h-4" />
                            <span>Get Shareable Link</span>
                        </button>
                        <button
                            onClick={handleDownloadPDF}
                            className="flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-colors shadow-sm hover:shadow-md font-medium text-sm"
                            style={{ backgroundColor: '#FF6B5A' }}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e55a4a'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#FF6B5A'; }}
                            title="Download as PDF"
                        >
                            <Printer className="w-4 h-4" />
                            <span>Download as PDF</span>
                        </button>
                    </div>
                </div>

                {/* Preview Content */}
                <div className="p-6">
                    <HtmlPreview
                        ref={previewRef}
                        htmlCode={htmlCode}
                        selectedElement={selectedElement}
                        onElementSelect={handleElementSelect}
                        isDragMode={isDragMode}
                        onHtmlChange={handleHtmlChange}
                        updateSource={updateSource}
                        isEditorMode={sidebarMode === 'edit'}
                    />
                </div>
            </div>

            {/* Save Changes Dialog */}
            <SaveChangesDialog
                isOpen={showSaveDialog}
                onSave={handleSaveChanges}
                onDiscard={handleDiscardChanges}
                onCancel={handleCancelClose}
                isSaving={isSaving}
            />

            {/* Shareable Link Modal */}
            {showLinkModal && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
                    onClick={() => setShowLinkModal(false)}
                >
                    <div
                        className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4"
                        onClick={(e) => e.stopPropagation()}
                        style={{ backgroundColor: '#F8F7F4' }}
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-xl font-semibold" style={{ color: '#0A0A10' }}>
                                Shareable Link
                            </h3>
                            <button
                                onClick={() => setShowLinkModal(false)}
                                className="p-1 rounded-full hover:bg-gray-200 transition-colors"
                                style={{ color: '#0A0A10' }}
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-2" style={{ color: '#0A0A10' }}>
                                Copy this link to share:
                            </label>
                            <div className="flex items-center gap-2 p-3 rounded-lg border" style={{ borderColor: '#D7B472', backgroundColor: 'white' }}>
                                <input
                                    type="text"
                                    value={shareableLink}
                                    readOnly
                                    className="flex-1 outline-none text-sm"
                                    style={{ color: '#0A0A10' }}
                                    onClick={(e) => e.target.select()}
                                />
                                <button
                                    onClick={handleCopyLink}
                                    className="p-2 rounded-lg transition-colors"
                                    style={{
                                        backgroundColor: linkCopied ? '#10b981' : '#2464f7',
                                        color: 'white'
                                    }}
                                    title="Copy link"
                                >
                                    {linkCopied ? (
                                        <Check className="w-4 h-4" />
                                    ) : (
                                        <Copy className="w-4 h-4" />
                                    )}
                                </button>
                            </div>
                            {linkCopied && (
                                <p className="text-sm mt-2" style={{ color: '#10b981' }}>
                                    ✓ Link copied to clipboard!
                                </p>
                            )}
                        </div>

                        <div className="flex justify-end">
                            <button
                                onClick={() => setShowLinkModal(false)}
                                className="px-4 py-2 rounded-lg font-medium text-sm transition-colors"
                                style={{
                                    backgroundColor: '#2464f7',
                                    color: 'white'
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#1e4ed8'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#2464f7'; }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style jsx>{`
        .draggable-mode .hover-target:hover {
          outline: 2px solid #22c55e !important;
          cursor: grab;
        }

        .dragging {
          opacity: 0.5;
          border: 2px dashed #22c55e;
        }

        .inspector-hovered {
          outline: 2px solid #3b82f6 !important;
          outline-offset: -2px;
          cursor: pointer;
          position: relative;
          z-index: 90;
          background-color: rgba(59, 130, 246, 0.05);
        }

        .inspector-selected {
          outline: 2px dashed #8b5cf6 !important;
          outline-offset: -2px;
          cursor: pointer;
          position: relative;
          z-index: 100;
        }
      `}</style>
        </div>
    );
}
